import { ActionMenuItem } from '@if/mui'
import { addMonths, addWeeks, isAfter, isValid, isWithinInterval, parse } from '@if/core/date'
import { EOriginSystemId, EPaymentFrequency, EPaymentMethod, EPaymentPlan } from '@if/core/types'
import { decimalToPercent, formatUtcIsoDateTime } from '@if/core/utils'
import { useCalculateTermAndPlanOptions } from '@src/data/api/worksheet-api/term-plan-api'
import { FormatPaymentPlanForList, getSelectedPlan, isDeliveryOnValid } from '@src/data/worksheet-selectors'
import { ComputeMonthlyTermsResponseDto } from '@src/types/ComputeMonthlyTermsResponseDto'
import { ETerms, MerchantId } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { MerchantPaymentPlan } from '@src/types/MerchantPaymentPlan'
import { SelectValueListItem } from '@src/types/SelectValueListItem'
import { CalculateTermPlanOptionsDto, TermAndPlanOption, TermPlanOptionDto } from '@src/types/TermPlanOptionDto'
import type { IfUser } from '@if/mui'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const usePaymentPlanList = (
  creditApp: CreditApplication,
  merchant: Merchant,
  paymentPlans?: MerchantPaymentPlan[],
) => {
  return useMemo(() => {
    const selectValueList: { label: string; value: string; id: string }[] = []

    selectValueList.push({
      value: EPaymentPlan.RegularDailyInterests,
      label: EPaymentPlan.RegularDailyInterests,
      id: merchant?.id,
    })

    const currentDate = new Date()
    const creationDate = new Date(creditApp?.createdOn)
    const DISTANT_FUTURE = new Date()
    DISTANT_FUTURE.setFullYear(DISTANT_FUTURE.getFullYear() + 100)

    const filteredPaymentPlans = paymentPlans?.filter((plan) => {
      const planActiveFrom = new Date(plan.activeFrom)
      const planExpiresOn = new Date(plan?.expiresOn ?? DISTANT_FUTURE)

      const currentUtcDateFormatted = new Date(formatUtcIsoDateTime(new Date()))
      return (
        ((isWithinInterval(creationDate, { start: planActiveFrom, end: planExpiresOn }) ||
          isWithinInterval(currentDate, { start: planActiveFrom, end: planExpiresOn })) &&
          isAfter(planExpiresOn, currentUtcDateFormatted)) ||
        creditApp.worksheet?.paymentPlanId === plan.id
      )
    })

    filteredPaymentPlans?.forEach((p) => {
      selectValueList.push({ value: p.id, label: FormatPaymentPlanForList(p), id: p.id })
    })

    return selectValueList
  }, [creditApp?.createdOn, creditApp.worksheet?.paymentPlanId, merchant?.id, paymentPlans])
}

export const usePaymentMerchantList = (
  creditApplication: CreditApplication,
  merchantsListFromParentId: Merchant[],
  merchant: Merchant | null,
  user: IfUser,
) => {
  const [merchantList, setMerchantList] = useState<Merchant[]>([])

  useEffect(() => {
    let newMerchantList: Merchant[] = []
    if (merchant?.forcePaymentToBorrower === true) {
      newMerchantList = [
        {
          id: MerchantId.PayCustomerByBankTransfer,
          defaultPaymentMethod: EPaymentMethod.BankTransfer,
          name: `${creditApplication.applicant.firstName} ${creditApplication.applicant.lastName}`,
        } as unknown as Merchant,
      ]
    } else if (creditApplication?.worksheet && user.isBannerUser) {
      const merchantIds = creditApplication.worksheet.merchantPayments.map((payment) => payment.merchantId)
      newMerchantList = merchantsListFromParentId.filter((m) => merchantIds.includes(m.id))
    } else if (merchant) {
      newMerchantList = [merchant]
    }
    return setMerchantList(newMerchantList)
  }, [creditApplication, merchant, merchantsListFromParentId, user.isBannerUser])
  return { merchantList, setMerchantList }
}

export const useProductsEarliestPaymentDate = () => {
  const getProductsEarliestPaymentDateGivenActivationDate = useCallback((activationDate: Date) => {
    const initialEarliestDateForFirstPayment = new Date(activationDate)
    initialEarliestDateForFirstPayment.setDate(initialEarliestDateForFirstPayment.getDate())
    return initialEarliestDateForFirstPayment
  }, [])

  return getProductsEarliestPaymentDateGivenActivationDate
}

export const useProductsFirstPaymentDateOptions = () => {
  const getProductsEarliestPaymentDateGivenActivationDate = useProductsEarliestPaymentDate()

  const computeProductsFirstPaymentDateOptions = useCallback(
    (activationDate: string, frequency: EPaymentFrequency) => {
      const activationDateAsDate = parse(activationDate, 'yyyy-MM-dd', new Date())
      const earliestPaymentDate = getProductsEarliestPaymentDateGivenActivationDate(activationDateAsDate)

      const firstPaymentDateOptionsList: Date[] = []
      const currentDate = new Date(earliestPaymentDate)
      const oneMonthLaterOrTwoWeeksLater =
        frequency === EPaymentFrequency.Monthly ? addMonths(earliestPaymentDate, 1) : addWeeks(earliestPaymentDate, 2)

      while (currentDate <= oneMonthLaterOrTwoWeeksLater) {
        if (isValid(currentDate)) {
          firstPaymentDateOptionsList.push(new Date(currentDate))
        }
        currentDate.setDate(currentDate.getDate() + 1)
      }

      return firstPaymentDateOptionsList
    },
    [getProductsEarliestPaymentDateGivenActivationDate],
  )

  return computeProductsFirstPaymentDateOptions
}

export const useTermOptionList = (termList: ETerms[]) => {
  const { t } = useTranslation()
  return useMemo(
    () =>
      termList.map((term) => (
        <ActionMenuItem key={term} value={term}>
          {term} {t('common.months')}
        </ActionMenuItem>
      )),
    [termList, t],
  )
}

interface UseLoanTermSelectionParams {
  possibleTerms: ComputeMonthlyTermsResponseDto | undefined
  creditApplication: CreditApplication
  merchant: Merchant
  paymentPlanId: EPaymentPlan | null
  term: number | null
  regularMinTerm: number
  /** Amount-based term ceiling for the current requested amount (see `getMaxTermForAmount`). */
  maxTermForAmount: number
  setValue: (name: 'term', value: number, options?: { shouldValidate?: boolean }) => void
  /**
   * The term & plan table owns the selection instead. No term is seeded, so the
   * worksheet opens with nothing chosen and the table waits for a real pick.
   */
  isBuydownEnabled?: boolean
}

// Shared loan-term selection for the Personal and IfXpress worksheets (#67242).
// Buy-down/promo plans can have terms below the standard minimum: the plan's own
// fixed term is injected into the dropdown (gated by the approved max-term check) so
// the field never blanks, and selecting a plan updates the term in the same render.
export const useLoanTermSelection = ({
  possibleTerms,
  creditApplication,
  merchant,
  paymentPlanId,
  term,
  regularMinTerm,
  maxTermForAmount,
  setValue,
  isBuydownEnabled = false,
}: UseLoanTermSelectionParams) => {
  const { t } = useTranslation()

  const maxTermDuration = creditApplication?.finalCreditDecision.maxTermDuration
  // Both ceilings bind, and the schema applies the same amount-based one, so the field
  // can never offer a term the worksheet would then refuse.
  const effectiveMaxTerm = Math.min(maxTermForAmount, maxTermDuration ?? maxTermForAmount)
  const isValidTerm = useCallback((value: number) => value <= effectiveMaxTerm, [effectiveMaxTerm])

  const loanTermChoicesList = useMemo(() => {
    const terms =
      possibleTerms?.monthlyTerms.reduce((acc: SelectValueListItem[], item) => {
        if (item.term >= (ETerms.twelveMonths as number) && isValidTerm(item.term)) {
          acc.push({ label: `${item.term} ${t('common.months')}`, value: item.term })
        }
        return acc
      }, []) || []

    if (paymentPlanId != null && paymentPlanId !== EPaymentPlan.RegularDailyInterests) {
      const selectedPlan = getSelectedPlan(merchant, paymentPlanId)
      if (
        selectedPlan &&
        isValidTerm(selectedPlan.loanTerm) &&
        !terms.some((choice) => choice.value === selectedPlan.loanTerm)
      ) {
        terms.push({ label: `${selectedPlan.loanTerm} ${t('common.months')}`, value: selectedPlan.loanTerm })
      }
    }

    return terms
  }, [possibleTerms, t, isValidTerm, paymentPlanId, merchant])

  const handlePaymentPlanChange = useCallback(
    (newPaymentPlanId: EPaymentPlan | null) => {
      if (!newPaymentPlanId) return
      const selectedPlan = getSelectedPlan(merchant, newPaymentPlanId)
      if (selectedPlan && isValidTerm(selectedPlan.loanTerm)) {
        setValue('term', selectedPlan.loanTerm, { shouldValidate: true })
        return
      }
      const isCurrentTermStillAcceptable = term != null && term >= regularMinTerm && isValidTerm(term)
      if (!isCurrentTermStillAcceptable) {
        setValue('term', regularMinTerm, { shouldValidate: true })
      }
    },
    [merchant, isValidTerm, setValue, term, regularMinTerm],
  )

  useEffect(() => {
    if (isBuydownEnabled) return
    handlePaymentPlanChange(paymentPlanId)
  }, [isBuydownEnabled, paymentPlanId, handlePaymentPlanChange])

  return { loanTermChoicesList, handlePaymentPlanChange }
}

const isRegularOption = (option: TermAndPlanOption): boolean => option.planLabel == null

export const useMappedTermAndPlanOptions = (rows: TermPlanOptionDto[] | null, isLoading: boolean) => {
  const options = useMemo<TermAndPlanOption[]>(() => {
    const mapped = (rows ?? []).map<TermAndPlanOption>((r) => ({
      key: `${r.paymentPlanId}|${r.term}`,
      paymentPlanId: r.paymentPlanId,
      termMonths: r.term,
      planLabel: r.planLabel ?? undefined,
      monthlyPayment: r.monthlyPayment,
      promoRate: decimalToPercent(r.promoRate),
      regularRate: decimalToPercent(r.regularRate),
      promoDurationMonths: r.promoDurationMonths ?? undefined,
      merchantFeeRate: decimalToPercent(r.merchantFeeRate),
      borrowerFeeRate: decimalToPercent(r.borrowerFeeRate),
      disabled: r.status === 'invalid',
      reason: r.reason ?? undefined,
      isBestTerm: r.isBestTerm,
      isDefault: r.isDefault,
    }))
    mapped.sort((a, b) => a.termMonths - b.termMonths || Number(isRegularOption(a)) - Number(isRegularOption(b)))
    return mapped
  }, [rows])

  return { options, isLoading }
}

interface UseTermAndPlanSelectionParams {
  isBuydownEnabled: boolean
  creditApplicationId?: string | null
  merchantId: string
  interestRate: number
  hasCoapplicant: boolean
  stateIso?: string | null
  paymentFrequency: EPaymentFrequency
  deliveryOn: string | null
  firstPaymentOn: string | null
  maxPmtAmount: number
  amountRequested: number
  includeInsurance: boolean
  paymentPlanId: EPaymentPlan
  term: number | null
}

export const useTermAndPlanSelection = ({
  isBuydownEnabled,
  creditApplicationId,
  merchantId,
  interestRate,
  hasCoapplicant,
  stateIso,
  paymentFrequency,
  deliveryOn,
  firstPaymentOn,
  maxPmtAmount,
  amountRequested,
  includeInsurance,
  paymentPlanId,
  term,
}: UseTermAndPlanSelectionParams) => {
  const dto = useMemo<CalculateTermPlanOptionsDto | null>(() => {
    if (!isBuydownEnabled || !deliveryOn || !firstPaymentOn || !amountRequested || !isDeliveryOnValid(deliveryOn))
      return null
    return {
      creditApplicationId,
      merchantId,
      interestRate,
      hasCoapplicant,
      stateIso: stateIso!,
      paymentFrequency,
      deliveryOn,
      firstPaymentOn,
      maxPmtAmount,
      amountRequested,
      includeInsurance,
      originSystemId: EOriginSystemId.MerchantDashboard,
    }
  }, [
    isBuydownEnabled,
    creditApplicationId,
    merchantId,
    interestRate,
    hasCoapplicant,
    stateIso,
    paymentFrequency,
    deliveryOn,
    firstPaymentOn,
    maxPmtAmount,
    amountRequested,
    includeInsurance,
  ])

  const [rows, isLoadingRows] = useCalculateTermAndPlanOptions(dto)
  const { options, isLoading } = useMappedTermAndPlanOptions(rows, isLoadingRows)

  const selectedOption = useMemo(
    () =>
      isBuydownEnabled
        ? options.find((o) => o.paymentPlanId === (paymentPlanId as string) && o.termMonths === term)
        : undefined,
    [isBuydownEnabled, options, paymentPlanId, term],
  )

  return { options, isLoading, selectedOption }
}
